import ThemedSelect from "../shared/ThemedSelect.jsx";
import { useCallback, useEffect, useState } from "react";
import { Badge, Button, ButtonGroup, Spinner, Table } from "react-bootstrap";
import { FaExclamationTriangle, FaSync } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import {
  clearErrorLogEntries,
  getErrorLogSummary,
  isAbortError,
  errorMessage,
  listErrorLog,
} from "../../services/apiClient.js";
import { formatDateTime } from "../../utils/date.js";

// Admin-only: what has actually broken, for whom, on which job.
//
// Two sources, kept visibly apart because they answer different questions
// (services/errorLog.js):
//   server  — a fault on the box. Something threw and the request died.
//   browser — a failure a user was SHOWN. The server may be perfectly happy;
//             job 76124's Zip All is the case that built this page, where the
//             archive was fine and the download was what failed.
//
// Read-only apart from Clear. There is nothing to edit here — a log you can
// correct is not evidence of anything.

const DAY_OPTIONS = [
  { value: 1, label: "Today" },
  { value: 7, label: "7 days" },
  { value: 30, label: "30 days" },
  { value: 90, label: "90 days" },
];

function ErrorLogPanel() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [days, setDays] = useState(7);
  const [source, setSource] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);

  const reload = useCallback(() => setReloadToken((n) => n + 1), []);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    Promise.all([
      listErrorLog({ days, source, limit: 500, signal: controller.signal }),
      getErrorLogSummary({ days, signal: controller.signal }),
    ])
      .then(([list, totals]) => {
        setRows(list);
        setSummary(totals);
        setError("");
      })
      .catch((err) => {
        if (isAbortError(err)) return;
        // A failed load must never render as an empty list — an error log that
        // shows nothing when it cannot load reads as "nothing has gone wrong",
        // which is the one wrong answer this page can give.
        setError(errorMessage(err, "Unable to load the error log."));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [days, source, reloadToken]);

  // Clear what is SHOWN, not what matches the filter (Robert 2026-08-07: the
  // natural time to clear is once the thing has been fixed). Two properties
  // follow from that, and both are the point:
  //   · Narrow the filter and you narrow what Clear can touch — so "the 12
  //     browser failures from today" can go while the server faults stay.
  //   · Anything logged since this page loaded was never in the list, so it
  //     survives. Clearing just after a fix is exactly when new entries are most
  //     likely to be arriving.
  // The warning names the count and the window, because "Clear" beside a filter
  // is otherwise a fair guess at "clear everything".
  async function clearShown() {
    const scope = source === "server" ? "server fault" : source === "browser" ? "user-facing failure" : "entry";
    const window_ = days === 1 ? "today" : `the last ${days} days`;
    if (
      !window.confirm(
        `Delete the ${rows.length} ${scope}${rows.length === 1 ? "" : "s"} shown from ${window_}?\n\n` +
          `This cannot be undone. Anything logged since this page loaded is not included.`
      )
    ) {
      return;
    }
    try {
      await clearErrorLogEntries(rows.map((row) => row.id));
      reload();
    } catch (err) {
      setError(errorMessage(err, "Unable to clear the error log."));
    }
  }

  return (
    <div>
      <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
        <h5 className="mb-0">
          <FaExclamationTriangle className="me-2 text-warning-emphasis" />
          Error Log
        </h5>

        <ButtonGroup size="sm">
          {DAY_OPTIONS.map((option) => (
            <Button
              key={option.value}
              variant={days === option.value ? "primary" : "outline-primary"}
              onClick={() => setDays(option.value)}
            >
              {option.label}
            </Button>
          ))}
        </ButtonGroup>

        <ThemedSelect
          size="sm"
          style={{ width: "auto" }}
          value={source}
          onChange={(event) => setSource(event.target.value)}
        >
          <option value="">Everything</option>
          <option value="server">Server faults</option>
          <option value="browser">What users were shown</option>
        </ThemedSelect>

        {summary ? (
          <span className="small text-body-secondary">
            {summary.total} in {days === 1 ? "the last day" : `the last ${days} days`}
            {summary.critical > 0 ? ` · ${summary.critical} CRITICAL` : ""}
            {summary.last_24h > 0 ? ` · ${summary.last_24h} in the last 24 hours` : ""}
            {summary.newest ? ` · newest ${formatDateTime(summary.newest)}` : ""}
          </span>
        ) : null}

        <span className="ms-auto d-flex gap-2">
          <Button size="sm" variant="outline-secondary" onClick={reload} disabled={loading}>
            <FaSync className={loading ? "me-1 fa-spin" : "me-1"} /> Refresh
          </Button>
          <Button
            size="sm"
            variant="outline-danger"
            onClick={clearShown}
            disabled={rows.length === 0 || loading}
            data-hint="Delete the entries currently listed — narrow the filter first to clear less"
          >
            Clear shown ({rows.length})
          </Button>
        </span>
      </div>

      {error ? <div className="alert alert-danger py-2">{error}</div> : null}

      {loading ? (
        <div className="text-center py-4">
          <Spinner animation="border" size="sm" /> <span className="ms-2">Loading…</span>
        </div>
      ) : rows.length === 0 && !error ? (
        <p className="text-body-secondary">Nothing has gone wrong in this window.</p>
      ) : (
        <Table size="sm" hover responsive className="align-middle">
          <thead>
            <tr>
              <th style={{ width: 150 }}>When</th>
              <th style={{ width: 90 }}>Source</th>
              <th style={{ width: 130 }}>User</th>
              <th style={{ width: 90 }}>Job</th>
              <th style={{ width: 170 }}>Page / Action</th>
              <th>Message</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.id}
                role="button"
                onClick={() => setExpanded(expanded === row.id ? null : row.id)}
                data-hint="Click for the full detail"
              >
                <td className="jl-num">{formatDateTime(row.occurred_at)}</td>
                <td>
                  <Badge bg={row.source === "server" ? "danger" : "secondary"}>{row.source}</Badge>
                  {/* CRITICAL = a scheduled task failed (or anything else that
                      must not wait to be browsed) — the future header red pill
                      reads these same rows. */}
                  {row.severity === "critical" ? (
                    <Badge bg="danger" className="ms-1 text-uppercase">critical</Badge>
                  ) : null}
                </td>
                <td>{row.username || <span className="text-body-secondary">—</span>}</td>
                <td>
                  {row.claim_number ? (
                    // Only a live claim can be opened; a number left behind by a
                    // deleted or re-imported job still reads, it just does not link.
                    row.claim_id ? (
                      <a
                        href={`/claims/${row.claim_number}`}
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          navigate(`/claims/${row.claim_number}`);
                        }}
                      >
                        {row.claim_number}
                      </a>
                    ) : (
                      row.claim_number
                    )
                  ) : (
                    <span className="text-body-secondary">—</span>
                  )}
                </td>
                <td className="small">
                  {row.action ? <div className="fw-semibold">{row.action}</div> : null}
                  <div className="text-body-secondary text-truncate" style={{ maxWidth: 170 }}>
                    {row.method ? `${row.method} ` : ""}
                    {row.page}
                    {row.status ? ` · ${row.status}` : ""}
                  </div>
                </td>
                <td className="small">
                  {row.message}
                  {expanded === row.id && row.detail ? (
                    <pre
                      className="mt-2 mb-0 p-2 small border rounded"
                      style={{ whiteSpace: "pre-wrap", maxHeight: 300, overflow: "auto" }}
                    >
                      {row.detail}
                    </pre>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
}

export default ErrorLogPanel;
